import { useInfiniteQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const PRODUCTS_PER_PAGE = 12;

interface Product {
  id: string;
  title: string;
  price_cents: number;
  currency: string;
  main_image_url?: string;
  discount_percentage?: number;
  discounted_price_cents?: number;
  discount_start_date?: string;
  discount_end_date?: string;
  has_variants?: boolean;
  variants?: Array<{ price_cents: number; quantity?: number }>;
}

export function useInfiniteProducts() {
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status,
  } = useInfiniteQuery({
    queryKey: ["infinite-products"],
    queryFn: async ({ pageParam = 0 }) => {
      const { data, error } = await supabase
        .from("products")
        .select(`
          id,
          title,
          price_cents,
          currency,
          main_image_url,
          discount_percentage,
          discounted_price_cents,
          discount_start_date,
          discount_end_date,
          has_variants,
          variants
        `)
        .eq("status", "published")
        .order("created_at", { ascending: false })
        .range(pageParam * PRODUCTS_PER_PAGE, (pageParam + 1) * PRODUCTS_PER_PAGE - 1);

      if (error) throw error;

      // Shuffle the results to create discovery experience
      const shuffledData = data ? [...data].sort(() => Math.random() - 0.5) : [];

      return {
        products: shuffledData,
        nextPage: data && data.length === PRODUCTS_PER_PAGE ? pageParam + 1 : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 0,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });

  // Flatten the pages into a single array
  const products = data?.pages.flatMap((page) => page.products) ?? [];

  return {
    products,
    isLoading: status === "pending",
    isError: status === "error",
    error,
    fetchNextPage,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
  };
}